import { normalizeChartingFinderFilters } from '@/features/charting-next/adapters/chartingFinderAdapters';
import { buildChartingFinderObject } from '@/features/charting-next/adapters/chartingPayloads';
import { legacyFinderToMarketQuery, marketQueryToLegacyFinder } from '@/features/stockchart/explorer/legacyFinderMapper';
import {
    MARKET_QUERY_PAGE_SIZE,
    MARKET_QUERY_SCHEMA_VERSION,
    MARKET_SELECTION_LABELS,
    MARKET_SELECTIONS,
    cloneMarketQuery,
    countActiveMarketFilters,
    createEmptyMarketQuery,
    firstMarketQueryPage,
    nextMarketQueryPage,
    normalizeMarketQuery,
    summarizeMarketQuery
} from '@/features/stockchart/explorer/marketQuery';

export {
    MARKET_QUERY_PAGE_SIZE,
    MARKET_QUERY_SCHEMA_VERSION,
    MARKET_SELECTION_LABELS,
    MARKET_SELECTIONS,
    cloneMarketQuery,
    countActiveMarketFilters,
    createEmptyMarketQuery,
    firstMarketQueryPage,
    nextMarketQueryPage,
    normalizeMarketQuery,
    summarizeMarketQuery
};

export const CHARTING_MARKET_SELECTIONS = Object.freeze(
    MARKET_SELECTIONS.map((id) => ({ id, label: MARKET_SELECTION_LABELS[id] }))
);

const CHARTING_MARKET_SORT_FIELDS = Object.freeze({
    ticker: 'ticker',
    entry_type: 'entry_type',
    instrument_number: 'instrument_number',
    in_price: 'entry_price',
    out_price: 'stop_price',
    support_price: 'support_price',
    support_date: 'support_date',
    current_price: 'current_price',
    tthh_profit: 'profitability',
    entry_distance: 'entry_distance',
    resistance: 'resistance',
    anual_var: 'annual_variation',
    wave_number: 'wave_number',
    next_entry_lt: 'next_entry',
    first_trading_date: 'first_trading_date',
    risk_reward: 'risk_reward',
    user_platinum_compatibility: 'compatibility'
});

export function chartingSortToMarketSort(value = 'original') {
    if (!value || value === 'original') return { field: 'original', direction: 'asc' };
    const [field, direction] = String(value).split(':');
    if (!CHARTING_MARKET_SORT_FIELDS[field]) return null;
    return { field: CHARTING_MARKET_SORT_FIELDS[field], direction: direction === 'desc' ? 'desc' : 'asc' };
}

export function marketSortToChartingSort(sort = {}) {
    if (!sort.field || sort.field === 'original') return 'original';
    const field = Object.keys(CHARTING_MARKET_SORT_FIELDS).find((key) => CHARTING_MARKET_SORT_FIELDS[key] === sort.field);
    return field ? `${field}:${sort.direction === 'desc' ? 'desc' : 'asc'}` : 'original';
}

export function isChartingMarketSortSupported(value) {
    return value === 'original' || Boolean(chartingSortToMarketSort(value));
}

export function chartingEntryToMarketQuery(entry = {}, routeQuery = {}) {
    const type = String(entry.type || 'finder');
    const value = String(entry.value || '');
    const query = createEmptyMarketQuery();

    if (type === 'finder') return { handled: true, restoreLast: true, query };
    if (type === 'saved-search') return { handled: true, savedName: value, query };
    if (type === 'platinum') query.selections = ['platinum'];
    else if (type === 'compatibility') {
        query.selections = ['compatibility'];
        if (routeQuery.currency) query.filters.currencies = [String(routeQuery.currency)];
    } else if (type === 'last-ipos') query.selections = ['ipo'];
    else if (type === 'best-bullish-values' && value === 'last_week') query.selections = ['bullish_week'];
    else if (type === 'best-bullish-values' && value === 'last_month') query.selections = ['bullish_month'];
    else if (type === 'hot-values' && value === 'last_week') query.selections = ['hot_week'];
    else if (type === 'description' && value) query.description = value;
    else return { handled: false, query: null };

    return { handled: true, restoreLast: false, query: normalizeMarketQuery(query) };
}

export function chartingFiltersToMarketQuery(filters, baseQuery = createEmptyMarketQuery(), baseFinder = {}) {
    const finder = buildChartingFinderObject(filters || {}, baseFinder || {});
    return legacyFinderToMarketQuery(finder, baseQuery);
}

export function marketQueryToChartingFilters(query, template = {}) {
    return normalizeChartingFinderFilters(marketQueryToLegacyFinder(query, template));
}

export function buildChartingMarketQueryCacheSignature(query) {
    return JSON.stringify(normalizeMarketQuery(query));
}
